<script setup>
import { Form } from "@primevue/forms"
import { useToast } from "primevue"
import Button from "primevue/button"
import Checkbox from "primevue/checkbox"
import FloatLabel from "primevue/floatlabel"
import InputText from "primevue/inputtext"
import Textarea from "primevue/textarea"
import { computed, ref } from "vue"

import { postConfirmationPresence } from "../api"

const emit = defineEmits(["update:modelValue", "submitted"])

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const toast = useToast()
const loading = ref(false)
const submitted = ref(false)

const hasAtLeastOneCheckbox = computed(() => {
  const m = props.modelValue
  return m.is_absent || m.secular_ceremony_presence || m.dinner_presence || m.sunday_brunch_presence
})

const buttonLabel = computed(() => {
  if (props.modelValue.is_absent) return "Confirmer mon absence"
  if (props.modelValue.secular_ceremony_presence || props.modelValue.dinner_presence || props.modelValue.sunday_brunch_presence) {
    return "Confirmer ma présence"
  }
  return "Valider ma réponse"
})

const updateModel = (key, value) => {
  emit("update:modelValue", {...props.modelValue, [key]: value})
}

const onAbsentChange = (value) => {
  const updates = { is_absent: value }
  if (value) {
    updates.secular_ceremony_presence = false
    updates.dinner_presence = false
    updates.sunday_brunch_presence = false
  }
  emit("update:modelValue", {...props.modelValue, ...updates})
}

const onPresenceChange = (key, value) => {
  const updates = { [key]: value }
  if (value) {
    updates.is_absent = false
  }
  emit("update:modelValue", {...props.modelValue, ...updates})
}

const resolver = () => {
  const errors = { first_name: [], last_name: [] }
  if (!props.modelValue.first_name) errors.first_name.push({ type: "required", message: "Merci de renseigner votre prénom" })
  if (!props.modelValue.last_name) errors.last_name.push({ type: "required", message: "Merci de renseigner votre nom" })
  return { errors }
}

const onFormSubmit = async({ valid }) => {
  submitted.value = true
  if (!valid || !hasAtLeastOneCheckbox.value) return
  loading.value = true
  try {
    await postConfirmationPresence(props.modelValue)
    emit("submitted", { isAbsent: props.modelValue.is_absent })
  } catch {
    toast.add({
      severity: "error",
      summary: "Erreur",
      detail: "Un problème est survenu, veuillez réessayer.",
      life: 5000,
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Form
    :resolver="resolver"
    class="presence-form"
    @submit="onFormSubmit"
  >
    <p class="presence-form__intro">
      Merci de nous indiquer si vous serez des nôtres pour célébrer ce jour si spécial.
    </p>

    <div class="presence-form__divider" />

    <div class="presence-form__row">
      <div class="presence-form__field">
        <FloatLabel variant="on">
          <InputText
            id="first_name"
            name="first_name"
            :model-value="modelValue.first_name"
            :invalid="submitted && !modelValue.first_name"
            fluid
            @update:model-value="(value) => updateModel('first_name', value)"
          />
          <label for="first_name">Prénom</label>
        </FloatLabel>
        <small
          v-if="submitted && !modelValue.first_name"
          class="presence-form__error"
        >
          Merci de renseigner votre prénom
        </small>
      </div>
      <div class="presence-form__field">
        <FloatLabel variant="on">
          <InputText
            id="last_name"
            name="last_name"
            :model-value="modelValue.last_name"
            :invalid="submitted && !modelValue.last_name"
            fluid
            @update:model-value="(value) => updateModel('last_name', value)"
          />
          <label for="last_name">Nom</label>
        </FloatLabel>
        <small
          v-if="submitted && !modelValue.last_name"
          class="presence-form__error"
        >
          Merci de renseigner votre nom
        </small>
      </div>
    </div>

    <div class="presence-form__divider" />

    <p class="presence-form__label">
      Je serai présent(e) pour&hellip;
    </p>

    <div class="presence-form__checkboxes">
      <div class="presence-form__checkbox-item">
        <Checkbox
          input-id="secular_ceremony"
          :model-value="modelValue.secular_ceremony_presence"
          binary
          @update:model-value="(value) => onPresenceChange('secular_ceremony_presence', value)"
        />
        <label
          for="secular_ceremony"
          class="presence-form__checkbox-label"
        >
          La cérémonie laïque
        </label>
      </div>

      <div class="presence-form__checkbox-item">
        <Checkbox
          input-id="dinner"
          :model-value="modelValue.dinner_presence"
          binary
          @update:model-value="(value) => onPresenceChange('dinner_presence', value)"
        />
        <label
          for="dinner"
          class="presence-form__checkbox-label"
        >
          Le dîner
        </label>
      </div>

      <div class="presence-form__checkbox-item">
        <Checkbox
          input-id="sunday_brunch"
          :model-value="modelValue.sunday_brunch_presence"
          binary
          @update:model-value="(value) => onPresenceChange('sunday_brunch_presence', value)"
        />
        <label
          for="sunday_brunch"
          class="presence-form__checkbox-label"
        >
          Le brunch du dimanche
        </label>
      </div>

      <div class="presence-form__divider" />

      <div class="presence-form__checkbox-item">
        <Checkbox
          input-id="is_absent"
          :model-value="modelValue.is_absent"
          binary
          @update:model-value="onAbsentChange"
        />
        <label
          for="is_absent"
          class="presence-form__checkbox-label"
        >
          Je ne pourrai pas être présent(e)
        </label>
      </div>
    </div>

    <small
      v-if="submitted && !hasAtLeastOneCheckbox"
      class="presence-form__error presence-form__error--centered"
    >
      Merci de cocher au moins une case
    </small>

    <div class="presence-form__divider" />

    <div class="presence-form__field">
      <FloatLabel variant="on">
        <Textarea
          id="diet_indications"
          name="diet_indications"
          :model-value="modelValue.diet_indications"
          rows="3"
          fluid
          @update:model-value="(value) => updateModel('diet_indications', value)"
        />
        <label for="diet_indications">Allergies ou régime alimentaire particulier</label>
      </FloatLabel>
    </div>

    <div class="presence-form__submit">
      <Button
        type="submit"
        :loading="loading"
        class="presence-form__button"
      >
        {{ buttonLabel }}
      </Button>
    </div>
  </Form>
</template>

<style lang="scss" scoped>
@import '../base/mixins';

.presence-form {
  display: flex;
  flex-direction: column;
  gap: $spacing-element;
}

.presence-form__intro {
  @include heading;
  font-size: 1.1rem;
  font-weight: 300;
  font-style: italic;
  text-align: center;
  color: $color-text-secondary;
  line-height: 1.6;
}

.presence-form__divider {
  width: 40px;
  height: 1px;
  background-color: $color-accent;
  margin: $spacing-element-sm auto;
}

.presence-form__row {
  display: grid;
  grid-template-columns: 1fr;
  gap: $spacing-element;

  @media (min-width: $bp-mobile) {
    grid-template-columns: 1fr 1fr;
  }
}

.presence-form__field {
  display: flex;
  flex-direction: column;
}

.presence-form__error {
  @include body-text;
  font-size: 0.75rem;
  color: #c0392b;
  margin-top: 4px;

  &--centered {
    text-align: center;
  }
}

.presence-form__label {
  @include heading;
  font-size: 1.05rem;
  font-weight: 400;
  text-align: center;
}

.presence-form__checkboxes {
  display: flex;
  flex-direction: column;
  gap: $spacing-element;
  padding: 0 $spacing-element-sm;
}

.presence-form__checkbox-item {
  display: flex;
  align-items: center;
  gap: $spacing-element;
}

.presence-form__checkbox-label {
  @include body-text;
  font-size: 0.95rem;
  cursor: pointer;
}

.presence-form__submit {
  display: flex;
  justify-content: center;
  margin-top: $spacing-element;
}

.presence-form__button {
  @include body-text;
  padding: 14px 40px;
  border: 1px solid $color-text-primary;
  background: transparent;
  color: $color-text-primary;
  font-size: 0.85rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: $color-text-primary;
    color: $color-bg-primary;
  }
}
</style>
